import { z } from 'zod';
import { defineManifest } from '@dashboard/module-contract/manifest';
import type { RolesOf } from '@dashboard/module-contract/manifest';

/**
 * Email.
 *
 * The object a user manages is a **domain they send from** — not an identity,
 * a configuration set, an event destination or a DKIM key pair (`003`). SES
 * sits midway along the abstraction spectrum: a domain is genuinely the user's
 * own object, while the machinery underneath it should never surface.
 */

export const settings = z.object({
	/**
	 * The subdomain SES bounces are addressed from. Always a custom one, which
	 * is what keeps SES's SPF record off the apex where it would collide with
	 * Google Workspace or anything else the client already sends through.
	 */
	mailFromLabel: z
		.string()
		.regex(/^[a-z0-9-]+$/)
		.default('mail'),
});

export type SesSettings = z.infer<typeof settings>;

export const manifest = defineManifest({
	id: 'ses',
	title: 'Email',
	roles: [
		{
			name: 'reader',
			title: 'Reader',
			description: 'View sending domains, their health and the suppression list',
		},
		{
			name: 'operator',
			title: 'Operator',
			description: 'Add and remove domains, configure signing and bounce handling',
			// "Everything above" was already the written description, and for a
			// while only the description: an operator without `reader` could
			// add a domain through the API and not list one.
			includes: ['reader'],
		},
		{
			// A third role, because an SMTP credential is a long-lived secret
			// that can send as the client indefinitely — a different kind of
			// action from adding a domain, not merely a bigger one.
			name: 'admin',
			title: 'Administrator',
			description: 'Everything above, plus creating and revoking SMTP credentials',
			includes: ['operator'],
		},
	],
	nav: [
		{ path: '', title: 'Domains', requiredRole: 'ses:reader' },
		{ path: 'activity', title: 'Delivery activity', requiredRole: 'ses:reader' },
		{ path: 'blocked', title: 'Blocked addresses', requiredRole: 'ses:reader' },
		{ path: 'credentials', title: 'SMTP credentials', requiredRole: 'ses:reader' },
		{ path: 'settings', title: 'Settings', requiredRole: 'ses:admin' },
	],
	settings,
});

/** Declared once, in the manifest. Every other surface derives from it. */
export type SesRole = RolesOf<typeof manifest>;
